import { type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import { ExternalLink } from '@repo/design-system-kit/components/external-link/external-link';
import { QontoLogo } from '@repo/design-system-kit/components/qonto-logo/qonto-logo';
import { DropdownLanguage } from '../../components/dropdown-language';
import { webviewManager } from '@repo/runtime-kit/webview';
import { useCurrentDevice } from '../../hooks/use-current-device';
import { useSegment } from '@repo/poly-hooks/segment';
import { appJsURL } from '@repo/runtime-kit/constants/hosts';
import { useSession } from '@repo/poly-hooks/session';
import logoQontoInvoicingDe from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-de.svg';
import logoQontoInvoicingEn from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-en.svg';
import logoQontoInvoicingEs from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-es.svg';
import logoQontoInvoicingFr from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-fr.svg';
import logoQontoInvoicingIt from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-it.svg';
import logoQontoInvoicingNl from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-nl.svg';
import logoQontoInvoicingPt from '@repo/visual-assets/illustrations/qonto-invoicing/logo-qonto-invoicing-pt.svg';
import { DougsPoweredByQontoLogo } from '@repo/design-system-kit/components/co-branded-logos/dougs/dougs-powered-by-qonto';
import { ItadvisorsXQontoLogo } from '@repo/design-system-kit/components/co-branded-logos/itadvisors/itadvisors-x-qonto';
import { DropdownOrganization } from '../../components/dropdown-organization';
import { DOUGS_PARTNER_FLOW_VARITAION } from '@repo/runtime-kit/constants/dougs';
import { ITADVISORS_PARTNER_FLOW_VARIATION } from '@repo/runtime-kit/constants/itadvisors';
import styles from './signup-topbar.module.css';

export type TopbarVariant = 'default' | 'qonto-invoicing' | 'dougs' | 'itadvisors';
type AuthVariant = 'organization-dropdown' | 'signout-button';

const SUPPORTED_INVOICING_LOCALES = ['de', 'en', 'es', 'fr', 'it', 'nl', 'pt'] as const;
type SupportedInvoicingLocale = (typeof SUPPORTED_INVOICING_LOCALES)[number];

const QONTO_INVOICING_LOGOS: Record<SupportedInvoicingLocale, string> = {
  de: logoQontoInvoicingDe,
  en: logoQontoInvoicingEn,
  es: logoQontoInvoicingEs,
  fr: logoQontoInvoicingFr,
  it: logoQontoInvoicingIt,
  nl: logoQontoInvoicingNl,
  pt: logoQontoInvoicingPt,
};

function isSupportedInvoicingLocale(language: string): language is SupportedInvoicingLocale {
  // oxlint-disable-next-line typescript/consistent-type-assertions
  return (SUPPORTED_INVOICING_LOCALES as readonly string[]).includes(language);
}

interface SignupTopbarProps {
  showSignIn?: boolean;
  children?: ReactNode;
  variant?: TopbarVariant;
  authVariant?: AuthVariant;
  /**
   * Keeps the Qonto logo visible on mobile. By default the (non-branded) logo
   * is hidden on mobile because the screen's top Back button fills that space;
   * screens without a Back button (country selector, resume, ghost, success)
   * opt back in via this prop. Branded variants always keep their logo.
   */
  keepLogoOnMobile?: boolean;
  /**
   * Mobile-only back button in the topbar's left slot. Pass a configured
   * `MobileBackButton` (or any node).
   */
  backButton?: ReactNode;
  /** Draws the mobile bottom border. */
  bordered?: boolean;
}

export function SignupTopbar({
  showSignIn = true,
  children,
  variant = 'default',
  authVariant = 'organization-dropdown',
  keepLogoOnMobile = false,
  backButton,
  bordered = true,
}: SignupTopbarProps): ReactNode {
  return (
    <nav className={cx(styles.nav, !bordered && styles.borderless)} data-test-signup-topbar>
      <div className={styles.leftContainer}>
        {backButton ? <div className={styles.mobileBackButton}>{backButton}</div> : null}
        <Logo keepLogoOnMobile={keepLogoOnMobile} variant={variant} />
      </div>
      {children}
      <div className={styles.rightContainer}>
        {showSignIn ? <SignInComponent authVariant={authVariant} /> : null}
        <DropdownLanguage className={styles.languagePicker} />
      </div>
    </nav>
  );
}

function Logo({
  variant,
  keepLogoOnMobile,
}: {
  variant: TopbarVariant;
  keepLogoOnMobile: boolean;
}): ReactNode {
  const { locale } = useIntl();

  if (variant === 'qonto-invoicing') {
    const language = locale.split('-')[0] ?? 'en';
    const supported: SupportedInvoicingLocale = isSupportedInvoicingLocale(language)
      ? language
      : 'en';
    const logoSrc = QONTO_INVOICING_LOGOS[supported];

    return (
      <div className={styles.logo} data-test-signup-topbar-logo>
        <img alt="" aria-hidden src={logoSrc} />
      </div>
    );
  }

  if (variant === DOUGS_PARTNER_FLOW_VARITAION) {
    return (
      <div className={styles.logo} data-test-signup-topbar-logo>
        {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx -- Dougs Powered by Qonto is a literal string */}
        <DougsPoweredByQontoLogo aria-label="Dougs Powered by Qonto" role="img" />
      </div>
    );
  }

  if (variant === ITADVISORS_PARTNER_FLOW_VARIATION) {
    return (
      <div
        className={cx(styles.logo, keepLogoOnMobile ? styles.smallOnMobile : styles.hideOnMobile)}
        data-test-signup-topbar-logo
      >
        {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx -- Itadvisors x Qonto is a literal string */}
        <ItadvisorsXQontoLogo aria-label="Itadvisors x Qonto" role="img" />
      </div>
    );
  }

  return (
    <div
      className={cx(styles.logo, keepLogoOnMobile ? styles.smallOnMobile : styles.hideOnMobile)}
      data-test-signup-topbar-logo
    >
      <QontoLogo />
    </div>
  );
}

function SignInComponent({ authVariant }: { authVariant: AuthVariant }): ReactNode {
  const session = useSession();

  if (session.isAuthenticated) {
    if (authVariant === 'signout-button') {
      return <SignOutButton />;
    }
    return <DropdownOrganization className={styles.dropdownOrganization} />;
  }
  return <SignInLink />;
}

function SignOutButton(): ReactNode {
  const session = useSession();

  const handleSignOut = (): void => {
    void session.invalidate();
  };

  return (
    <Button onPress={handleSignOut} type="button" variant="tertiary">
      <FormattedMessage id="generic.sign-out.cta" />
    </Button>
  );
}

function SignInLink() {
  const { isMobileBrowser } = useCurrentDevice();
  const segment = useSegment();

  const trackClickOnMobileBrowserOnly = (): void => {
    if (isMobileBrowser) {
      segment.track('onboarding_sign_in_app_clicked');
    }
  };

  const signInUrl = isMobileBrowser ? `${appJsURL}/detectapp.html` : appJsURL;

  if (!webviewManager.isWebview) {
    return (
      <ExternalLink
        data-test-signup-topbar-signin
        display="button"
        href={signInUrl}
        onClick={trackClickOnMobileBrowserOnly}
        target="_self"
        variant="tertiary"
      >
        {isMobileBrowser ? (
          <FormattedMessage id="btn.sign_in_via_app" />
        ) : (
          <FormattedMessage id="generic.sign-in.cta" />
        )}
      </ExternalLink>
    );
  }

  // Is WebView, means running within the Qonto mobile app
  return null;
}
